import { bunja } from 'bunja';
import { subDays } from 'date-fns';
import { atom } from 'jotai';
import { atomWithInfiniteQuery, atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';

import {
  getNotificationGroups,
  getUserNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  updateNotificationGroup,
} from '@flipster/apis/v1/user';
import { UpdateNotificationGroupReq } from '@flipster/apis/v1/user/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { convertDate } from '@flipster/utils/datetime';
import { getErrorMessage } from '@flipster/utils/error';

export const userNotificationsQueryKey = ['user', 'notifications'];
export const userNotificationGroupsQueryKey = [...userNotificationsQueryKey, 'group'];

export const notificationCenterBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const notificationsBaseAtom = atomWithInfiniteQuery(() => ({
    queryKey: userNotificationsQueryKey,
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      getUserNotifications({
        cursor: pageParam,
        direction: 'desc',
        count: 20,
      }),
    enabled: store.get(isCookieLoggedInAtom),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      // nextPageCursor가 없으면 undefined 반환
      if (!lastPage.data.nextPageCursor) {
        return undefined;
      }

      // 마지막 페이지에 알림이 있는 경우 30일 제한 확인
      if (lastPage.data.objects.length > 0) {
        const oldestNotification = lastPage.data.objects[lastPage.data.objects.length - 1];
        const thirtyDaysAgo = subDays(new Date(), 30);
        const notificationDate = convertDate(oldestNotification.createdAt);

        // 가장 오래된 알림이 30일보다 오래되었다면 더 이상 가져오지 않음
        if (notificationDate && notificationDate < thirtyDaysAgo) {
          return undefined;
        }
      }

      return lastPage.data.nextPageCursor;
    },
    refetchInterval: 1000 * 15,
  }));

  const notificationsAtom = atom((get) => {
    const allNotifications = get(notificationsBaseAtom).data?.pages.flatMap((page) => page.data.objects) || [];
    const thirtyDaysAgo = subDays(new Date(), 30);
    return allNotifications.filter((notification) => {
      const notificationDate = convertDate(notification.createdAt);
      return notificationDate && notificationDate >= thirtyDaysAgo;
    });
  });
  const hasNextPageAtom = atom((get) => get(notificationsBaseAtom).hasNextPage);
  const isFetchingNextPageAtom = atom((get) => get(notificationsBaseAtom).isFetchingNextPage);

  const fetchNextPage = () => {
    const { fetchNextPage: fetcher, hasNextPage } = store.get(notificationsBaseAtom);
    if (hasNextPage) {
      fetcher();
    }
  };

  const markAllAsReadMutationAtom = atomWithMutation((get) => ({
    mutationFn: markAllNotificationsAsRead,
    onSettled() {
      const queryClient = get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: userNotificationsQueryKey });
    },
  }));

  const markAllAsRead = async () => {
    const firstNotificationId = store.get(notificationsBaseAtom).data?.pages[0].data.objects[0].id;
    if (firstNotificationId) {
      try {
        await store.get(markAllAsReadMutationAtom).mutateAsync(firstNotificationId);
      } catch (error) {
        snackbar.error({ message: getErrorMessage(error) });
      }
    }
  };

  const markNotificationAsReadMutationAtom = atomWithMutation((get) => ({
    mutationFn: (notificationId: string) => markNotificationAsRead(notificationId),
    onSettled() {
      const queryClient = get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: userNotificationsQueryKey });
    },
  }));

  const notificationGroupsQueryAtom = atomWithQuery(() => ({
    queryKey: userNotificationGroupsQueryKey,
    queryFn: getNotificationGroups,
    select: (res) => res.data.notificationGroups,
    refetchOnMount: true,
    enabled: store.get(isCookieLoggedInAtom),
  }));

  /* 설정 페이지 알림 그룹 관리 */
  const notificationGroupsAtom = atom((get) => get(notificationGroupsQueryAtom).data);
  const updateNotificationGroupMutationAtom = atomWithMutation((get) => ({
    mutationFn: ({ groupCode, body }: { groupCode: string; body: UpdateNotificationGroupReq }) =>
      updateNotificationGroup(groupCode, body),
    onSettled(_data, _error, variables: { groupCode: string; body: UpdateNotificationGroupReq }) {
      const queryClient = get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: userNotificationGroupsQueryKey });
      if (variables.groupCode.startsWith('p')) {
        queryClient.invalidateQueries({ queryKey: ['getPromotionSubscriptionsApi'] });
      }
    },
    onError(error) {
      snackbar.error({ message: getErrorMessage(error) });
    },
  }));

  const isNotificationCenterOpenAtom = atom(false);

  return {
    notificationsAtom,
    hasNextPageAtom,
    isFetchingNextPageAtom,
    fetchNextPage,
    markAllAsRead,
    isLoadingAtom: atom((get) => get(notificationsBaseAtom).isLoading),
    isErrorAtom: atom((get) => get(notificationsBaseAtom).isError),
    notificationGroupsAtom,
    updateNotificationGroupMutationAtom,
    notificationGroupsQueryAtom,
    markNotificationAsReadMutationAtom,
    isNotificationCenterOpenAtom,
  };
});
